import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { observable, action, runInAction, makeObservable } from "mobx";
import { API } from "General/API";
import { availableAccessRightsStore } from "./AvailableAccessRightsStore";

export class AccessRightsStore extends CoreStore {
  allRights: API_FolderRightsEntry[] = [];
  groupRights: API_FolderRightsEntry[] = [];
  usersRights: API_FolderRightsEntry[] = [];
  rightsPerUser: API_AccessUserRight[] = [];
  copyState: StoreState = "RAW";
  private folderId: number;

  constructor() {
    super();

    makeObservable(this, {
      allRights: observable,
      groupRights: observable,
      usersRights: observable,
      rightsPerUser: observable,
      copyState: observable,
      clear: action,
      init: action.bound,
      fetch: action.bound,
      fetchPerUser: action.bound,
      deleteRight: action.bound,
      addRight: action.bound,
      copyAccessRights: action.bound,
    });
  }

  clear() {
    this.groupRights = [];
    this.usersRights = [];
    this.rightsPerUser = [];
    this.copyState = "RAW";
    this.folderId = undefined;
  }

  url(id?: number) {
    return API_URL.FOLDERS.addFolderRights(projectsDataStore.projectId, id || this.folderId);
  }

  async init(folderId: number) {
    try {
      await this.fetch(folderId);
      runInAction(() => {
        this.folderId = folderId;
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async fetch(id?: number) {
    try {
      if (availableAccessRightsStore.state === "RAW") {
        await availableAccessRightsStore.fetch();
      }
      const response: API_FolderRightsEntry[] = await API.getData(this.url(id || this.folderId));
      runInAction(() => {
        const { groupRights, usersRights } = this.processResponse(response);
        this.allRights = response;
        this.groupRights = groupRights;
        this.usersRights = usersRights;
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async fetchPerUser() {
    try {
      const response: API_AccessUserRight[] = await API.getData(
        API_URL.FOLDERS.getFolderUserRights(projectsDataStore.projectId, this.folderId)
      );
      runInAction(() => {
        this.rightsPerUser = response;
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async deleteRight(rightId: number) {
    try {
      await API.deleteData(
        API_URL.FOLDERS.deleteProjectMailAccessRight(projectsDataStore.projectId, this.folderId, rightId)
      );
      await this.fetch();
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async addRight(body: API_FolderRightsEntry) {
    try {
      await API.newData(this.url(), body);
      await this.fetch();
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState<AccessRightsStore>("copyState")
  async copyAccessRights() {
    try {
      return await API.newData<API_StringData>(
        API_URL.FOLDERS.copyFolderRights(projectsDataStore.projectId, this.folderId),
        {}
      );
    } catch (error) {
      throw error;
    }
  }

  processResponse = (response: API_FolderRightsEntry[]) => {
    const groupRights: API_FolderRightsEntry[] = [],
      usersRights: API_FolderRightsEntry[] = [];
    response.forEach((item) => {
      item.assigneeId.startsWith("@group") ? groupRights.push(item) : usersRights.push(item);
    });

    return { groupRights, usersRights };
  };
}

export const accessRightsStore = new AccessRightsStore();
